import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  CALL_OUT_OF_JURISDICTION,
  CALL_REROUTED,
} from '@rsos/constants/collaborator';
import { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import {
  clearConversation,
  TalkJSMessageSource,
} from '@rsos/reduxDataStore/src/features/talkjsMessages/talkjsMessagesSlice';
import { useGetConversationMessagesMutation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import { useAppDispatch } from '@rsos/utils/reduxHooks';
import { RootState } from '../app';
import { selectCallQueueBasedOnAniAliAccess } from '../selectors/callQueueSelectorNext';

interface ConversationDescriptor {
  conversationId: string;
  callerId: string;
  source: TalkJSMessageSource;
}

// Throttle/poll cadence for the CSV refresh. The download endpoint is
// relatively expensive (it walks TalkJS for each id), so a 60s window is
// the floor we want. Search UX is fine with this freshness because the
// slice is only used as a substring search index.
const FETCH_INTERVAL_MS = 60_000;

/**
 * Walk the filtered call queue + the TF911 slice and produce a flat list
 * of `{conversationId, callerId, source}` we should fetch messages for.
 * De-duplicates by conversationId so the same id surfacing in multiple
 * slices (e.g. tf911 + collaborator) only triggers one fetch.
 */
function collectConversationDescriptors(
  callQueue: Record<string, EmergencyCall> | undefined,
  tf911Conversations: Record<string, { data?: { conversation_id?: string } }>,
): ConversationDescriptor[] {
  const seen = new Set<string>();
  const descriptors: ConversationDescriptor[] = [];

  const add = (descriptor: ConversationDescriptor) => {
    if (!descriptor.conversationId) return;
    if (seen.has(descriptor.conversationId)) return;
    seen.add(descriptor.conversationId);
    descriptors.push(descriptor);
  };

  if (callQueue) {
    for (const callerId of Object.keys(callQueue)) {
      const call = callQueue[callerId];
      if (!call) continue;

      // rti_downstream shares this same conversationID field, but chat
      // parity for it isn't built yet (tracked separately) -- only
      // fetch messages for the two collaborator types.
      const collabId =
        (call.collaborator?.type === CALL_REROUTED ||
          call.collaborator?.type === CALL_OUT_OF_JURISDICTION) &&
        call.collaborator?.conversationID;
      if (collabId) {
        add({
          conversationId: collabId,
          callerId,
          source: 'collaborator',
        });
      }

      const agencyId = call.agencyShareChat?.conversationID;
      if (agencyId) {
        add({
          conversationId: agencyId,
          callerId,
          source: 'agencyShare',
        });
      }
    }
  }

  if (tf911Conversations) {
    for (const callerId of Object.keys(tf911Conversations)) {
      const conversationId =
        tf911Conversations[callerId]?.data?.conversation_id;
      if (!conversationId) continue;
      // Only surface TF911 conversations tied to a call still in the
      // (filtered) queue — keeps fetch churn bounded as calls roll off
      // the queue.
      if (callQueue && !callQueue[callerId]) continue;
      add({ conversationId, callerId, source: 'tf911' });
    }
  }

  return descriptors;
}

/**
 * Pulls TalkJS message history for every conversation tied to a valid
 * call and mirrors the text into the `talkjsMessages` slice. The slice
 * is consumed by `GlobalMapSearch` so users can substring-search every
 * message visible from the call queue.
 *
 * Reuses the existing `/conversation-history/download` endpoint (the
 * same one the messaging admin's conversation history uses for the CSV
 * export). The
 * response is parsed from CSV inside the RTK mutation's responseHandler
 * — no new backend route required.
 *
 * Practical limits inherited from that endpoint:
 *   - Active TF911 conversations 403 (BE filters sessions to
 *     status=INACTIVE). The hook silently drops those.
 *   - Collaborator / agency-share rooms 403 for the same reason (no
 *     TF911 session row). Filtered out before fetch to avoid the noise.
 *   - One id per request so a single 403 doesn't poison a whole batch.
 *     Worst-case cost is N requests per `FETCH_INTERVAL_MS` where N is
 *     the number of TF911 descriptors in the queue.
 */
const useLoadCallQueueMessages = () => {
  const dispatch = useAppDispatch();
  const [getConversationMessages] = useGetConversationMessagesMutation();

  const callQueue = useSelector(selectCallQueueBasedOnAniAliAccess);
  const tf911Conversations = useSelector(
    (state: RootState) => state.textFrom911?.conversations,
  );
  const orgName = useSelector(
    (state: RootState) => state.sinatra?.user?.currentOrg?.name,
  );

  const descriptors = useMemo(
    () =>
      collectConversationDescriptors(
        callQueue as Record<string, EmergencyCall> | undefined,
        tf911Conversations ?? {},
      ),
    [callQueue, tf911Conversations],
  );

  // The download endpoint can only resolve TF911 sessions; collaborator
  // and agency-share rooms have no row in the TF911 session table and
  // would always 403. Strip them before issuing requests so we don't
  // waste RTT or pollute Sentry with expected failures.
  const fetchableDescriptors = useMemo(
    () => descriptors.filter(d => d.source === 'tf911'),
    [descriptors],
  );

  const descriptorKey = useMemo(
    () =>
      fetchableDescriptors
        .map(d => `${d.conversationId}|${d.callerId}|${d.source}`)
        .sort()
        .join(','),
    [fetchableDescriptors],
  );

  // Track which conversation ids we've already pushed into the slice so
  // we can issue `clearConversation` for ones that drop off between
  // renders.
  const knownIdsRef = useRef<Set<string>>(new Set());

  // Wall-clock time of the most recent successful (or attempted) fetch.
  // Survives effect re-runs so a flurry of descriptor changes can't push
  // us over the FETCH_INTERVAL_MS budget.
  const lastFetchAtRef = useRef<number>(0);

  useEffect(() => {
    let cancelled = false;
    let initialTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let pollIntervalId: ReturnType<typeof setInterval> | undefined;

    const desiredById = new Map(
      fetchableDescriptors.map(d => [d.conversationId, d]),
    );

    // Drop slice entries for any conversation that fell off the queue.
    // Snapshot first so we can safely mutate the set mid-iteration (and
    // to keep TS happy on lower lib targets that don't iterate Sets).
    const knownIds = Array.from(knownIdsRef.current);
    for (const id of knownIds) {
      if (!desiredById.has(id)) {
        knownIdsRef.current.delete(id);
        dispatch(clearConversation({ conversationId: id }));
      }
    }

    if (fetchableDescriptors.length === 0 || !orgName) {
      return () => {
        cancelled = true;
      };
    }

    const fetchOne = async (
      descriptor: ConversationDescriptor,
    ): Promise<void> => {
      try {
        // Pass `callerId` + `source` through even though the network
        // request only uses `conversationId`/`orgName`. The
        // `talkjsMessages` slice listens on this mutation's
        // `matchFulfilled` and reads those fields off
        // `meta.arg.originalArgs` to stamp records — so a successful
        // unwrap here is enough; no follow-up dispatch needed.
        await getConversationMessages({
          orgName,
          conversationId: descriptor.conversationId,
          callerId: descriptor.callerId,
          source: descriptor.source,
        }).unwrap();
      } catch {
        // Skip silently — the slice just won't index that conversation.
        // Reporting to Sentry is owned by rtkQueryErrorMiddleware, which
        // sees every rsosApi rejection: it reports real outages (5xx,
        // parse errors) and skips the expected/self-healing cases this
        // endpoint tags via transformErrorResponse (403, FETCH_ERROR,
        // TIMEOUT_ERROR). A second captureException here would just
        // double-count.
        return;
      }

      if (cancelled) return;
      knownIdsRef.current.add(descriptor.conversationId);
    };

    const fetchAll = async () => {
      if (cancelled) return;
      lastFetchAtRef.current = Date.now();
      // allSettled because individual 403s should never abort the batch.
      await Promise.allSettled(fetchableDescriptors.map(fetchOne));
    };

    // Throttle the very first fetch so a descriptor flurry can't burst
    // past one round per FETCH_INTERVAL_MS, then poll at that cadence to
    // keep the search index warm.
    const elapsed = Date.now() - lastFetchAtRef.current;
    const initialDelay = Math.max(0, FETCH_INTERVAL_MS - elapsed);

    const startPolling = () => {
      void fetchAll();
      pollIntervalId = setInterval(() => {
        void fetchAll();
      }, FETCH_INTERVAL_MS);
    };

    if (initialDelay === 0) {
      startPolling();
    } else {
      initialTimeoutId = setTimeout(startPolling, initialDelay);
    }

    return () => {
      cancelled = true;
      if (initialTimeoutId) clearTimeout(initialTimeoutId);
      if (pollIntervalId) clearInterval(pollIntervalId);
    };
    // descriptorKey collapses the dependency to "the actual conversation
    // set changed", avoiding refetches when the underlying selectors just
    // return new object identities. `fetchableDescriptors` is captured by
    // closure and is consistent with descriptorKey for the same render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptorKey, dispatch, orgName, getConversationMessages]);
};

export default useLoadCallQueueMessages;
